import {Color, Icon} from '@benchling/nucleus-ui';
import {css, StyleSheet} from 'aphrodite';
import React, {useEffect, useRef, useState} from 'react';

import ModelTypeConstants from 'constants/ModelTypeConstants';
import {StudyType} from 'graphql-client/__generated/graphqlGlobalTypes';
import {QueryResultType, useBenchlingQuery} from 'graphql-client/BenchlingQuery';
import {isInaccessible} from 'graphql-client/InaccessibleInfo';
import {SectionWrapper} from 'metadata/MetadataEditorInfoSection';
import EditableAnalysisList from 'studies/search/EditableAnalysisList';
import {getProcedureRunUIStringForStudyType} from 'studies/TypedStudyUtils';
import {ChipSize} from 'ui-library/chips/Chip';
import ProcedureChip from 'ui-library/chips/ProcedureChip';
import CollapsibleCard from 'ui-library/CollapsibleCard';
import {GRAY_LIGHT, WHITE} from 'ui-library/Colors';
import {CenteredHorizontalThrobber} from 'ui-library/HorizontalThrobber';
import * as DateUtil from 'util/DateUtil';

import TableOfContents from '../shared/TableOfContents';
import {StudyInaccessibleContentBanner, StudyQueryErrorBanner} from '../utils/ui/StudyQueryBanners';
import {
  ProcessDevelopmentStudyExecutionProgressQuery,
  ProcessDevelopmentStudyExecutionProgressQueryVariables,
  ProcessDevelopmentStudyExecutionProgressQuery_study_StudyNode_procedureRuns_ProcedureRunNode as ProcedureRunNode,
} from './__generated/ProcessDevelopmentStudyExecutionProgressQuery';
import NoExperimentOverview from './NoExperimentOverview';
import {ProcedureRunParamsTable} from './ProcedureRunParamsTable';
import {
  buildProcessDevelopmentStudyExecutionProgress,
  ProcedureRunOverview,
  PROCESS_DEVELOPMENT_STUDY_EXECUTION_PROGRESS_QUERY,
} from './useProcessDevelopmentStudyProgress';

const VIEWPORT_BOX_OFFSET = 40; // offset to lower the top of the viewport box
const EXPERIMENT_CARD_MARGIN = 16;

export default function MultiExperimentOverviewProgress({
  studyId,
  studyType,
}: {
  studyId: string;
  studyType: StudyType;
}): JSX.Element {
  const result = useBenchlingQuery<
    ProcessDevelopmentStudyExecutionProgressQuery,
    ProcessDevelopmentStudyExecutionProgressQueryVariables
  >(PROCESS_DEVELOPMENT_STUDY_EXECUTION_PROGRESS_QUERY, {variables: {studyId}});

  if (result.type === QueryResultType.PENDING) {
    return (
      <>
        <div className={css(styles.loadingDiv)} />
        <CenteredHorizontalThrobber />
      </>
    );
  } else if (result.type === QueryResultType.ERROR) {
    return <StudyQueryErrorBanner />;
  } else {
    const {data} = result;
    if (isInaccessible(data.study)) {
      return (
        <StudyInaccessibleContentBanner
          inaccessibleInfo={data.study}
          modelType={ModelTypeConstants.STUDY}
          displayErrorText="This study is no longer accessible. Try refreshing the page if this is unexpected."
        />
      );
    }
    const procedureRuns = data.study.procedureRuns.filter(
      (procedureRun) =>
        !isInaccessible(procedureRun) && !isInaccessible(procedureRun.templateProcedureFlowchartVersion)
    ) as Array<ProcedureRunNode>;
    if (procedureRuns.length === 0 && isInaccessible(data.study.procedureRuns[0])) {
      return (
        <StudyInaccessibleContentBanner
          inaccessibleInfo={data.study.procedureRuns[0]}
          modelType={ModelTypeConstants.PROCEDURE_RUN}
          displayErrorText={`The ${getProcedureRunUIStringForStudyType(data.study.studySchema.studyType, {plural: true})} of this study are not accessible. The ${getProcedureRunUIStringForStudyType(data.study.studySchema.studyType, {plural: true})} associated with this study may have been moved into a project that you do not have access to.`}
        />
      );
    }
    const overviews = procedureRuns.map((procedureRun) =>
      buildProcessDevelopmentStudyExecutionProgress(procedureRun)
    );
    return (
      <RenderMultiExperimentOverviewProgress
        studyId={data.study.id}
        studyType={data.study.studySchema.studyType}
        overviews={overviews}
      />
    );
  }
}

function RenderExperimentCards({
  studyId,
  overviews,
}: {
  studyId: string;
  overviews: Array<ProcedureRunOverview>;
}): JSX.Element {
  return (
    <div>
      {overviews.map((overview, index) => (
        <div key={index} id={`experimentCard_${index}`} className={css(styles.experimentCardSpacing)}>
          <CollapsibleCard
            leftHeader={<div className={css(styles.experimentName)}>{overview.name} </div>}
            initiallyExpanded={true}
            isCollapsible={false}
            headerColor={WHITE}
          >
            <div className={css(styles.paramsTableInCard)}>
              <div className={css(styles.cardComponents, styles.gridContainer)}>
                <div style={{gridColumnStart: 1, gridColumnEnd: 3}}>
                  <SectionWrapper headerName="Created">
                    <div className={css(styles.createdDateTimeDisplay)}>
                      {DateUtil.readableTime(overview.createdAt, {showTime: true})}
                    </div>
                  </SectionWrapper>
                </div>
                <div style={{gridColumnStart: 3, gridColumnEnd: 6}}>
                  <SectionWrapper headerName="Assay">
                    <ProcedureChip id={overview.procedureTemplateId} size={ChipSize.MINI} />
                    <TemplateLinkInfo />
                  </SectionWrapper>
                </div>
                <div style={{gridColumnStart: 6, gridColumnEnd: 11}}>
                  <SectionWrapper headerName="Analysis">
                    <EditableAnalysisList procedureRunId={overview.procedureRunId} />
                  </SectionWrapper>
                </div>
              </div>
              <ProcedureRunParamsTable
                studyId={studyId}
                overview={overview}
                isMultiExperimentEnabled={true}
              />
            </div>
          </CollapsibleCard>
        </div>
      ))}
    </div>
  );
}

function RenderMultiExperimentOverviewProgress({
  studyId,
  studyType,
  overviews,
}: {
  studyId: string;
  studyType: StudyType;
  overviews: Array<ProcedureRunOverview>;
}): JSX.Element {
  const experiments = overviews.map((overview, index) => ({
    title: overview.name,
    id: `experimentCard_${index}`,
  }));
  const childRef = useRef<HTMLDivElement>(null);
  // ensures that the ref is set before rendering the TOC
  // ToC uses UseDocumentScrollState which requires the ref to be set
  // to check if elements are in the in view of the container
  const [isRefSet, setIsRefSet] = useState(false);

  useEffect(() => {
    if (childRef.current) {
      setIsRefSet(true);
    }
  }, []);

  return (
    <div className={css(styles.cardsAndTocContainer)}>
      {isRefSet && childRef.current && (
        <div className={css(styles.toc)}>
          <TableOfContents
            title="EXPERIMENTS"
            contents={experiments}
            container={childRef.current}
            contentsMarginTop={EXPERIMENT_CARD_MARGIN}
            viewportBoxOffset={VIEWPORT_BOX_OFFSET}
            emptyText="Experiments added to this study will appear here"
          />
        </div>
      )}
      <div className={css(styles.experimentCardContainer)} ref={childRef}>
        {overviews.length === 0 ? (
          <div className={css(styles.noExperimentContainer)}>
            <NoExperimentOverview studyId={studyId} studyType={studyType} />
          </div>
        ) : (
          <RenderExperimentCards studyId={studyId} overviews={overviews} />
        )}
      </div>
    </div>
  );
}

/**
 * Render a message indicating that the experiment may be using an earlier version of the linked template.
 *
 * @package exported for testing
 */
export function TemplateLinkInfo(): JSX.Element {
  return (
    <div className={css(styles.templateLinkInfo)}>
      <Icon icon="info-circle" className={css(styles.templateLinkInfoIcon)} />
      This experiment may be using an earlier version of the linked assay
    </div>
  );
}

const styles = StyleSheet.create({
  toc: {
    display: 'flex',
    backgroundColor: WHITE,
    width: '13%',
    minWidth: 200,
    paddingTop: 30,
    paddingLeft: 20,
    paddingRight: 10,
    borderRight: `1.5px solid ${GRAY_LIGHT}`,
    position: 'sticky',
    top: '0',
    height: '100%',
    minHeight: '100%',
    overflowY: 'auto',
    overflowX: 'hidden',
  },
  cardsAndTocContainer: {
    display: 'flex',
    height: '100%',
  },
  createdDateTimeDisplay: {
    marginTop: 12,
  },
  cardComponents: {
    display: 'flex',
    flexDirection: 'row',
    marginBottom: -17,
    marginTop: 8,
    marginLeft: 2,
  },
  loadingDiv: {
    paddingBottom: 5,
  },
  experimentName: {
    fontSize: 16,
  },
  paramsTableInCard: {
    width: '97%',
    padding: 5,
  },
  gridContainer: {
    display: 'grid',
    gridTemplateColumns: 'repeat(10, minmax(0, 1fr))',
  },
  experimentCardSpacing: {
    margin: EXPERIMENT_CARD_MARGIN,
  },
  experimentCardContainer: {
    overflowX: 'hidden',
    scrollBehavior: 'smooth',
    position: 'relative',
    width: '100%',
  },
  templateLinkInfo: {
    color: Color.GRAY_600,
    display: 'flex',
    marginTop: 5,
    marginRight: 25,
  },
  templateLinkInfoIcon: {
    color: Color.GRAY_500,
    marginTop: 3,
    marginRight: 5,
  },
  noExperimentContainer: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
    height: '100%',
    width: '100%',
    position: 'absolute',
  },
});
